import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { Camera, MagnifyingGlass, X as XIcon, ArrowSquareOut, Sparkle, CurrencyDollar, Funnel } from "@phosphor-icons/react";

const KIND_LABEL = { before: "Before", after: "After", receipt: "Receipt", appliance: "Appliance" };
const KIND_COLOR = {
  before: "bg-slate-700",
  after: "bg-emerald-600",
  receipt: "bg-amber-600",
  appliance: "bg-indigo-600",
};

export default function PropertyPhotoArchive({ address, unit: propUnit = "", jobId: propJobId = "" }) {
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ kind: "", tag: "", room: "", worker: "", q: "" });
  const [lightbox, setLightbox] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  // Scope toggle: default to WO if we're inside a WO, unit if a unit was passed,
  // otherwise the whole building.
  const initialScope = propJobId ? "wo" : (propUnit ? "unit" : "building");
  const [scope, setScope] = useState(initialScope);
  const [scopeUnit, setScopeUnit] = useState(propUnit || "");

  useEffect(() => {
    if (!address) return;
    const params = { address, limit: 400 };
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    if (scope === "wo" && propJobId) params.job_id = propJobId;
    else if (scope === "unit") params.unit = scopeUnit || propUnit || "";
    api.get("/properties/photos", { params })
      .then(r => setData(r.data))
      .catch(() => setData({ total: 0, matches: 0, photos: [], facets: {} }));
  }, [address, filters, scope, scopeUnit, propUnit, propJobId]);

  if (!data) return <div className="rugged-card p-4 bg-white text-sm font-bold text-slate-500">Loading archive…</div>;

  const facets = data.facets || {};
  const photos = data.photos || [];
  const unitOptions = facets.units || [];

  const clearFilters = () => setFilters({ kind: "", tag: "", room: "", worker: "", q: "" });
  const hasFilters = Object.values(filters).some(v => v);

  const scopeLabel = scope === "wo" ? "This WO" : scope === "unit" ? `Unit ${scopeUnit || propUnit || "—"}` : "Whole Building";

  return (
    <div className="rugged-card p-4 bg-white" data-testid="property-photo-archive">
      <div className="flex items-center gap-2 flex-wrap">
        <Camera weight="bold" size={20}/>
        <h2 className="font-display font-black text-lg uppercase tracking-wider">Photo Archive</h2>
        <span className="text-xs text-slate-500 font-bold" data-testid="archive-counts">
          {data.matches} of {data.total} photos{hasFilters ? " (filtered)" : ""}
          {facets.kind && (
            <>
              {" · "}
              <span className="text-slate-700 font-black">{facets.kind.before || 0} before</span>
              {" · "}
              <span className="text-emerald-700 font-black">{facets.kind.after || 0} after</span>
              {" · "}
              <span className="text-amber-700 font-black">{facets.kind.receipt || 0} receipts</span>
              {facets.kind.appliance ? <> · <span className="text-indigo-700 font-black">{facets.kind.appliance} appliance</span></> : null}
            </>
          )}
        </span>
        <button onClick={() => setShowFilters(v => !v)}
          data-testid="archive-filters-toggle"
          className={`ml-auto h-9 px-3 border-2 border-[#0f172a] font-black uppercase text-[10px] tracking-wider inline-flex items-center gap-1 ${showFilters ? "bg-[#0f172a] text-white" : "bg-white"}`}>
          <Funnel weight="bold" size={12}/> Filters
        </button>
        {hasFilters && (
          <button onClick={clearFilters}
            data-testid="archive-clear-filters"
            className="h-9 px-2 border-2 border-red-500 text-red-600 font-black uppercase text-[10px] tracking-wider">
            Clear
          </button>
        )}
      </div>

      {/* Scope toggle — narrow the count from "everything at this address"
          down to a single unit or a single WO. Solves the "why does FR say
          160 photos when AppFolio shows 12 on this WO?" confusion. */}
      <div className="mt-3 flex items-center gap-1.5 flex-wrap" data-testid="archive-scope-row">
        <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 mr-1">Scope</span>
        <button
          type="button"
          onClick={() => setScope("building")}
          data-testid="archive-scope-building"
          className={`h-8 px-3 border-2 font-black uppercase text-[10px] tracking-widest rounded-full transition-colors ${scope === "building" ? "bg-[#0f172a] text-white border-[#0f172a]" : "bg-white text-slate-700 border-slate-300 hover:border-[#0f172a]"}`}>
          Whole Building
        </button>
        {(unitOptions.length > 0 || propUnit) && (
          unitOptions.length > 1 ? (
            <div className={`inline-flex border-2 rounded-full overflow-hidden ${scope === "unit" ? "border-[#0f172a]" : "border-slate-300"}`}>
              <button
                type="button"
                onClick={() => { setScope("unit"); if (!scopeUnit && unitOptions[0]) setScopeUnit(unitOptions[0]); }}
                data-testid="archive-scope-unit"
                className={`h-8 px-3 font-black uppercase text-[10px] tracking-widest ${scope === "unit" ? "bg-[#0f172a] text-white" : "bg-white text-slate-700"}`}>
                Unit
              </button>
              <select
                value={scopeUnit}
                onChange={e => { setScope("unit"); setScopeUnit(e.target.value); }}
                data-testid="archive-scope-unit-picker"
                className={`h-8 pl-2 pr-6 text-xs font-black font-mono border-l-2 ${scope === "unit" ? "bg-white border-[#0f172a]" : "bg-white border-slate-300"}`}>
                {unitOptions.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => { setScope("unit"); setScopeUnit(unitOptions[0] || propUnit); }}
              data-testid="archive-scope-unit"
              className={`h-8 px-3 border-2 font-black uppercase text-[10px] tracking-widest rounded-full transition-colors ${scope === "unit" ? "bg-[#0f172a] text-white border-[#0f172a]" : "bg-white text-slate-700 border-slate-300 hover:border-[#0f172a]"}`}>
              Unit {unitOptions[0] || propUnit}
            </button>
          )
        )}
        {propJobId && (
          <button
            type="button"
            onClick={() => setScope("wo")}
            data-testid="archive-scope-wo"
            className={`h-8 px-3 border-2 font-black uppercase text-[10px] tracking-widest rounded-full transition-colors ${scope === "wo" ? "bg-[#0f172a] text-white border-[#0f172a]" : "bg-white text-slate-700 border-slate-300 hover:border-[#0f172a]"}`}>
            This WO
          </button>
        )}
        <span className="ml-auto text-[10px] font-black uppercase tracking-widest text-slate-500" data-testid="archive-scope-label">
          Showing: {scopeLabel}
        </span>
      </div>

      {/* Search bar always visible */}
      <div className="mt-3 relative">
        <MagnifyingGlass weight="bold" size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"/>
        <input value={filters.q} onChange={e => setFilters(f => ({ ...f, q: e.target.value }))}
          placeholder="Search notes, tags, damage descriptions, appliance models…"
          data-testid="archive-search"
          className="w-full h-10 pl-8 pr-3 border-2 border-[#0f172a] text-sm font-medium"/>
      </div>

      {showFilters && (
        <div className="mt-3 p-3 border-2 border-dashed border-slate-300 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="archive-filter-panel">
          <FilterSelect label="Kind" value={filters.kind} testid="archive-filter-kind"
            options={facets.kinds || []} render={KIND_LABEL}
            onChange={v => setFilters(f => ({ ...f, kind: v }))}/>
          <FilterSelect label="Tag" value={filters.tag} testid="archive-filter-tag"
            options={facets.tags || []}
            onChange={v => setFilters(f => ({ ...f, tag: v }))}/>
          <FilterSelect label="Room" value={filters.room} testid="archive-filter-room"
            options={facets.rooms || []}
            onChange={v => setFilters(f => ({ ...f, room: v }))}/>
          <FilterSelect label="Worker" value={filters.worker} testid="archive-filter-worker"
            options={facets.workers || []}
            onChange={v => setFilters(f => ({ ...f, worker: v }))}/>
        </div>
      )}

      {photos.length === 0 && (
        <div className="mt-4 p-6 text-center text-slate-500 font-bold text-sm" data-testid="archive-empty">
          {data.total === 0 ? "No photos on file at this property yet." : "No matches for those filters."}
        </div>
      )}

      {photos.length > 0 && (
        <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2" data-testid="archive-grid">
          {photos.map((p, i) => {
            const isVideo = p.media_type === "video" || (p.content_type || "").startsWith("video/");
            return (
              <button key={`${p.job_id}-${p.file_id}-${i}`} onClick={() => setLightbox(p)}
                data-testid={`archive-photo-${p.file_id}`}
                className="relative aspect-square border-2 border-[#0f172a] overflow-hidden bg-slate-100 group text-left">
                {isVideo ? (
                  <video src={fileUrl(p.path)} className="w-full h-full object-cover" muted preload="metadata"/>
                ) : (
                  <img src={fileUrl(p.path)} alt="" className="w-full h-full object-cover" loading="lazy"/>
                )}
                <span className={`absolute top-1 left-1 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-white ${KIND_COLOR[p.kind] || "bg-slate-600"}`}>
                  {KIND_LABEL[p.kind] || p.kind}
                </span>
                {p.chargeable_to_tenant && (
                  <span className="absolute top-1 right-1 px-1 py-0.5 text-[9px] font-black uppercase bg-red-600 text-white inline-flex items-center gap-0.5">
                    <CurrencyDollar weight="bold" size={10}/>
                  </span>
                )}
                {p.ai_tags && p.ai_tags.length > 0 && (
                  <span className="absolute bottom-1 left-1 px-1 py-0.5 text-[9px] font-black uppercase bg-violet-600 text-white inline-flex items-center gap-0.5">
                    <Sparkle weight="fill" size={9}/>
                  </span>
                )}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors"/>
              </button>
            );
          })}
        </div>
      )}

      {lightbox && (
        <PhotoLightbox photo={lightbox} onClose={() => setLightbox(null)}/>
      )}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, render, testid }) {
  return (
    <div>
      <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)}
        data-testid={testid}
        className="mt-0.5 w-full h-9 px-2 border-2 border-[#0f172a] text-xs font-medium bg-white truncate">
        <option value="">All</option>
        {options.map(o => (
          <option key={o} value={o}>{render ? render[o] || o : o}</option>
        ))}
      </select>
    </div>
  );
}

function PhotoLightbox({ photo, onClose }) {
  const isVideo = photo.media_type === "video" || (photo.content_type || "").startsWith("video/");
  return (
    <div className="fixed inset-0 z-[90] bg-black/85 flex items-center justify-center p-3" onClick={onClose} data-testid="archive-lightbox">
      <div className="w-full max-w-4xl bg-white border-4 border-white max-h-[95vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-2 sm:p-3 flex items-center justify-between bg-[#0f172a] text-white">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <span className={`px-1.5 py-0.5 text-[10px] font-black uppercase tracking-wider ${KIND_COLOR[photo.kind] || "bg-slate-600"}`}>{KIND_LABEL[photo.kind] || photo.kind}</span>
            {photo.chargeable_to_tenant && (
              <span className="px-1.5 py-0.5 text-[10px] font-black uppercase tracking-wider bg-red-600 inline-flex items-center gap-0.5">
                <CurrencyDollar weight="bold" size={11}/> TENANT $
              </span>
            )}
            <span className="text-xs font-bold truncate">{photo.job_title}</span>
          </div>
          <button onClick={onClose} data-testid="archive-lightbox-close"
            className="h-8 w-8 border-2 border-white flex items-center justify-center flex-shrink-0">
            <XIcon weight="bold" size={16}/>
          </button>
        </div>
        <div className="bg-black flex items-center justify-center">
          {isVideo ? (
            <video src={fileUrl(photo.path)} controls className="max-h-[65vh] w-full"/>
          ) : (
            <img src={fileUrl(photo.path)} alt="" className="max-h-[65vh] w-auto"/>
          )}
        </div>
        <div className="p-3 sm:p-4 space-y-2 text-sm">
          {photo.ai_note && (
            <div className="p-2 bg-violet-50 border-l-4 border-violet-600">
              <div className="text-[10px] font-black uppercase tracking-wider text-violet-800 inline-flex items-center gap-1">
                <Sparkle weight="fill" size={11}/> AI Note
              </div>
              <div className="mt-1 text-slate-700 text-xs">{photo.ai_note}</div>
            </div>
          )}
          {photo.damage_description && (
            <div className="p-2 bg-red-50 border-l-4 border-red-600">
              <div className="text-[10px] font-black uppercase tracking-wider text-red-800">Damage</div>
              <div className="mt-1 text-slate-700 text-xs">{photo.damage_description}</div>
            </div>
          )}
          {(photo.tags?.length > 0 || photo.ai_tags?.length > 0) && (
            <div className="flex flex-wrap gap-1">
              {[...(photo.tags || []), ...(photo.ai_tags || [])].map((t, i) => (
                <span key={i} className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-white ${(photo.ai_tags || []).includes(t) ? "bg-violet-600" : "bg-[#0f172a]"}`}>
                  {t}
                </span>
              ))}
            </div>
          )}
          {photo.appliance_brand && (
            <div className="text-xs text-slate-700">
              <b>Appliance:</b> {photo.appliance_type} · {photo.appliance_brand} {photo.appliance_model}
            </div>
          )}
          <div className="grid grid-cols-2 gap-2 text-[11px] pt-2 border-t border-slate-200">
            {photo.inspection_room && <div><b className="text-slate-500 uppercase text-[9px]">Room:</b> {photo.inspection_room}</div>}
            {photo.work_order_number && <div><b className="text-slate-500 uppercase text-[9px]">WO#:</b> {photo.work_order_number}</div>}
            {photo.uploaded_by_name && <div><b className="text-slate-500 uppercase text-[9px]">By:</b> {photo.uploaded_by_name}</div>}
            {photo.uploaded_at && <div><b className="text-slate-500 uppercase text-[9px]">On:</b> {new Date(photo.uploaded_at).toLocaleDateString()}</div>}
          </div>
          <Link to={`/admin/jobs/${photo.job_id}`}
            data-testid="archive-lightbox-open-job"
            className="mt-2 tactile-btn w-full h-11 bg-[#00C4CC] text-white font-black uppercase text-sm tracking-wider inline-flex items-center justify-center gap-2">
            <ArrowSquareOut weight="bold" size={16}/> Open Source Job
          </Link>
        </div>
      </div>
    </div>
  );
}
